import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatChartTick, formatKstDateTime } from './iot_metrics';
import {
    APP_METRICS, COMMAND_LABELS, INITIAL_IR_STATE, TERMINAL_STATUSES, commandKey, createIotAppClient,
    formatAppMetric, irUnavailableReason, isIotPrototypeHost, parseClaimQr, validateClaim
} from './iot_app_client';
import './iot_app_prototype.css';

const CONNECTION_LABELS = { ONLINE: '온라인', OFFLINE: '오프라인', NEVER_CONNECTED: '연결 대기', DISABLED: '사용 중지' };
const POWER = [['OFF', '꺼짐'], ['ON', '켜짐']];
const MODES = [['COOL', '냉방'], ['HEAT', '난방'], ['DRY', '제습'], ['FAN', '송풍'], ['AUTO', '자동']];
const FANS = [['LOW', '약'], ['MEDIUM', '중'], ['HIGH', '강'], ['AUTO', '자동']];
const canceled = (error) => error.name === 'AbortError';

function useRemote(load) {
    const [revision, setRevision] = useState(0);
    const [result, setResult] = useState({ loading: true, data: null, error: '' });
    const reload = useCallback(() => setRevision((value) => value + 1), []);
    useEffect(() => {
        const controller = new AbortController();
        setResult({ loading: true, data: null, error: '' });
        load(controller.signal).then((data) => {
            if (!controller.signal.aborted) setResult({ loading: false, data, error: '' });
        }).catch((error) => {
            if (!controller.signal.aborted && !canceled(error)) setResult({ loading: false, data: null, error: error.message });
        });
        return () => controller.abort();
    }, [load, revision]);
    return { ...result, reload };
}

function useAction() {
    const lock = useRef(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    async function run(task) {
        if (lock.current) return;
        lock.current = true;
        setBusy(true);
        setError('');
        try { await task(); }
        catch (failure) { if (!canceled(failure)) setError(failure.message); }
        finally { lock.current = false; setBusy(false); }
    }
    return { busy, error, run };
}

function Alert({ children }) {
    return children ? <p className="iap-alert" role="alert">{children}</p> : null;
}
function Loading({ children = '장치 정보를 불러오는 중입니다.' }) {
    return <p className="iap-empty" role="status">{children}</p>;
}
function RemoteState({ result }) {
    return result.loading ? <Loading /> : result.error ? <div><Alert>{result.error}</Alert><button className="iap-secondary" onClick={result.reload}>다시 시도</button></div> : null;
}
function DeviceSummary({ device }) {
    return <section className="iap-card iap-device-summary">
        <span className={`iap-status ${device.connection_state === 'ONLINE' ? 'online' : ''}`}>{CONNECTION_LABELS[device.connection_state] || '상태 확인 필요'}</span>
        <h2>{device.display_name || device.device_id}</h2>
        <p>{device.location || '위치 미설정'}</p>
        <p className="iap-device-id">{device.device_id}</p>
        <div className="iap-meta"><span>{device.role === 'OWNER' ? '내 장치' : device.role === 'MEMBER' ? '공유받은 장치' : '직원 조회'}</span><span>최근 통신 {formatKstDateTime(device.last_seen_at)}</span></div>
    </section>;
}

function Login({ client, onLogin, notice }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [kind, setKind] = useState('customer');
    const action = useAction();
    return <section className="iap-login">
        <div className="iap-brand" aria-hidden="true">↗</div>
        <h2>내 공간의 공기를<br />웹에서도 확인하세요.</h2>
        <p className="iap-muted">STG 고객 또는 직원 계정으로 로그인해 주세요.</p>
        <Choices label="로그인 계정" values={[[ 'customer', '고객' ], [ 'staff', '직원' ]]} selected={kind} disabled={action.busy} onChange={(value) => { setKind(value); setEmail(''); setPassword(''); }} />
        <Alert>{notice || action.error}</Alert>
        <form onSubmit={(event) => { event.preventDefault(); action.run(async () => {
            const suppliedPassword = password;
            setPassword('');
            onLogin(await (kind === 'staff' ? client.loginStaff(email, suppliedPassword) : client.login(email, suppliedPassword)));
        }); }}>
            <label>{kind === 'staff' ? '직원 아이디' : '고객 아이디(이메일)'}<input name="username" type={kind === 'staff' ? 'text' : 'email'} autoComplete="username" required maxLength={100} value={email} onChange={(event) => setEmail(event.target.value)} /></label>
            <label>비밀번호<input name="password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
            <button className="iap-primary" disabled={action.busy}>{action.busy ? '로그인 중…' : '로그인'}</button>
        </form>
        <p className="iap-footnote">{kind === 'staff' ? '관리자 페이지에서 사용하는 직원 아이디로 장치의 센서를 조회하고 IR 제어를 사용할 수 있습니다.' : '카카오 로그인·회원가입은 이 테스트 페이지에 포함되지 않습니다.'}</p>
    </section>;
}

function Hub({ client, go, staff = false }) {
    const load = useCallback(async (signal) => {
        const [devices, invitations] = await Promise.all([client.devices(signal), staff ? Promise.resolve({ invitations: [] }) : client.incoming(signal)]);
        return { devices: devices.devices, invitations: invitations.invitations };
    }, [client, staff]);
    const result = useRemote(load);
    const action = useAction();
    return <>
        {staff ? <p className="iap-notice">직원 계정 · 장치를 선택해 센서 조회와 IR 제어를 사용할 수 있습니다.</p> : <section className="iap-card iap-register-card"><h2>새 IoT 장치 등록</h2><p>제품 QR을 스캔하거나 장치 ID를 직접 입력하세요.</p><div className="iap-row"><button className="iap-primary" onClick={() => go('claim', null, true)}>QR 스캔</button><button className="iap-secondary" onClick={() => go('claim')}>직접 입력</button></div></section>}
        <div className="iap-section-heading"><h2>{staff ? '전체 장치' : '내 장치'}</h2><button className="iap-text-button" disabled={result.loading || action.busy} onClick={result.reload}>새로고침</button></div>
        <RemoteState result={result} />
        <Alert>{action.error}</Alert>
        {result.data?.devices.length === 0 && <p className="iap-empty">아직 등록된 IoT 장치가 없습니다.{!staff && <><br />제품 QR 또는 장치 ID로 장치를 추가해 주세요.</>}</p>}
        {result.data?.devices.map((device) => <button key={device.device_id} className="iap-device-button" onClick={() => go('detail', device.device_id)} aria-label={`${device.display_name || device.device_id} 상세 보기`}><DeviceSummary device={device} /><span className="iap-chevron" aria-hidden="true">›</span></button>)}
        {!staff && <h2 className="iap-section-heading">받은 초대</h2>}
        {!staff && result.data?.invitations.length === 0 && <p className="iap-muted">받은 가족 공유 초대가 없습니다.</p>}
        {result.data?.invitations.map((invitation) => <section className="iap-card" key={invitation.invitation_id}><h3>{invitation.device_id}</h3><p>가족 공유 초대가 도착했습니다.</p><div className="iap-row"><button className="iap-primary" disabled={action.busy} onClick={() => action.run(async () => { await client.respond(invitation.invitation_id, true); result.reload(); })}>수락</button><button className="iap-secondary" disabled={action.busy} onClick={() => action.run(async () => { await client.respond(invitation.invitation_id, false); result.reload(); })}>거절</button></div></section>)}
    </>;
}

function Claim({ client, go, scanInitially }) {
    const [deviceId, setDeviceId] = useState('');
    const [scanning, setScanning] = useState(scanInitially);
    const [cameraError, setCameraError] = useState('');
    const video = useRef(null);
    const action = useAction();
    useEffect(() => {
        if (!scanning) return undefined;
        let stopped = false;
        let stream;
        let timer;
        async function start() {
            try {
                if (!window.BarcodeDetector || !navigator.mediaDevices?.getUserMedia
                    || !(await window.BarcodeDetector.getSupportedFormats()).includes('qr_code')) {
                    throw new Error('unsupported');
                }
                stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
                if (stopped) { stream.getTracks().forEach((track) => track.stop()); return; }
                video.current.srcObject = stream;
                await video.current.play();
                const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
                async function detect() {
                    if (stopped) return;
                    try {
                        const found = await detector.detect(video.current);
                        if (stopped) return;
                        if (found.length) {
                            try {
                                const data = parseClaimQr(found[0].rawValue);
                                setDeviceId(data.device_id); setScanning(false); setCameraError('');
                                return;
                            } catch (error) { setCameraError(error.message); }
                        }
                    } catch (_) { /* Retry only camera frames, never an API mutation. */ }
                    if (!stopped) timer = setTimeout(detect, 300);
                }
                detect();
            } catch (_) {
                if (!stopped) { setCameraError('카메라를 사용할 수 없습니다. 장치 ID를 직접 입력해 주세요.'); setScanning(false); }
            }
        }
        start();
        return () => { stopped = true; clearTimeout(timer); stream?.getTracks().forEach((track) => track.stop()); };
    }, [scanning]);
    return <>
        <h2>IoT 장치 등록</h2><p className="iap-muted">제품 QR을 스캔하거나 모듈의 장치 ID를 입력하세요.</p>
        <button className="iap-secondary" onClick={() => { setCameraError(''); setScanning(!scanning); }}>{scanning ? '카메라 닫기' : 'QR 스캔'}</button>
        {scanning && <video className="iap-camera" ref={video} muted playsInline aria-label="제품 QR 카메라" />}
        <Alert>{cameraError || action.error}</Alert>
        <form onSubmit={(event) => { event.preventDefault(); action.run(async () => {
            const data = validateClaim(deviceId);
            setScanning(false);
            const response = await client.claim(data);
            go('detail', response.device.device_id);
        }); }}>
            <label>장치 ID<input required maxLength={64} autoComplete="off" value={deviceId} onChange={(event) => setDeviceId(event.target.value)} /></label>
            <p className="iap-footnote">처음 연결하는 모듈은 전원을 켠 뒤 등록해 주세요.</p>
            <button className="iap-primary" disabled={action.busy}>{action.busy ? '등록 중…' : '장치 등록'}</button>
        </form>
    </>;
}

function Choices({ label, values, selected, onChange, disabled }) {
    return <fieldset className="iap-choices" disabled={disabled}><legend>{label}</legend><div>{values.map(([value, title]) => <button type="button" key={value} aria-pressed={selected === value} onClick={() => onChange(value)}>{title}</button>)}</div></fieldset>;
}

function IrControl({ client, deviceId, refresh, visible }) {
    const load = useCallback((signal) => client.control(deviceId, signal), [client, deviceId]);
    const result = useRemote(load);
    const [state, setState] = useState({ ...INITIAL_IR_STATE });
    const [command, setCommand] = useState(null);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const active = useRef(null);
    const edited = useRef(false);
    const firstRefresh = useRef(refresh);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; active.current?.abort(); };
    }, []);
    useEffect(() => {
        if (firstRefresh.current !== refresh && !active.current) { result.reload(); firstRefresh.current = refresh; }
    }, [refresh, result.reload]);
    useEffect(() => {
        if (result.data && !edited.current) setState({ ...(result.data.last_successful?.state || INITIAL_IR_STATE) });
    }, [result.data]);
    useEffect(() => {
        if (!visible || busy || result.loading || result.error
            || result.data?.capability?.enabled !== true || result.data?.capability?.protocol_version !== 1
            || result.data?.connection?.telemetry_state === 'DISABLED'
            || result.data?.connection?.control_ready === true) return undefined;
        const timer = setTimeout(result.reload, 2000);
        return () => clearTimeout(timer);
    }, [visible, busy, result.loading, result.error, result.data, result.reload]);

    const poll = useCallback(async (first, signal) => {
        let current = first;
        if (!signal.aborted) setCommand(current);
        for (let attempt = 0; attempt < 15 && !TERMINAL_STATUSES.includes(current?.status); attempt += 1) {
            if (!current?.command_id || !['PENDING', 'PUBLISHED'].includes(current.status)) break;
            await new Promise((resolve) => {
                const done = () => { clearTimeout(timer); signal.removeEventListener('abort', done); resolve(); };
                const timer = setTimeout(done, 1000);
                signal.addEventListener('abort', done, { once: true });
                if (signal.aborted) done();
            });
            if (signal.aborted) return;
            current = (await client.command(deviceId, current.command_id, signal)).command;
            if (!signal.aborted) setCommand(current);
        }
        if (!signal.aborted && !TERMINAL_STATUSES.includes(current?.status)) setMessage('결과 확인이 지연되고 있습니다. 새로고침 후 확인해 주세요.');
    }, [client, deviceId]);

    // Resume observing an existing command after refresh; never resubmit it.
    useEffect(() => {
        const id = result.data?.active_command_id;
        if (!id || active.current) return undefined;
        const controller = new AbortController();
        active.current = controller;
        setBusy(true); setMessage('');
        (async () => {
            try { await poll((await client.command(deviceId, id, controller.signal)).command, controller.signal); }
            catch (error) { if (!canceled(error) && !controller.signal.aborted) setMessage('결과 확인이 지연되고 있습니다. 새로고침 후 확인해 주세요.'); }
            finally {
                if (active.current === controller) active.current = null;
                if (!controller.signal.aborted && mounted.current) { setBusy(false); }
            }
        })();
        return () => { controller.abort(); if (active.current === controller) active.current = null; };
    }, [result.data, client, deviceId, poll]);

    const reason = irUnavailableReason(result.data);
    const disabled = result.loading || busy || Boolean(reason);
    const editingDisabled = result.loading || busy;
    async function send() {
        if (disabled || active.current) return;
        const controller = new AbortController();
        active.current = controller;
        setBusy(true); setMessage(''); setCommand(null);
        try {
            const response = await client.send(deviceId, { ...state }, commandKey(), controller.signal);
            await poll(response.command, controller.signal);
        } catch (error) {
            if (!canceled(error) && !controller.signal.aborted) setMessage('전송 결과를 확인하지 못했습니다. 새로고침 후 확인해 주세요.');
        } finally {
            if (active.current === controller) active.current = null;
            if (!controller.signal.aborted && mounted.current) { setBusy(false); result.reload(); }
        }
    }
    const update = (key, value) => { edited.current = true; setState((previous) => ({ ...previous, [key]: value })); };
    return <section className="iap-card iap-control" aria-label="IR 제어">
        <div className="iap-section-heading"><h2>에어컨 제어</h2><button className="iap-text-button" disabled={busy || result.loading} onClick={() => { setMessage(''); result.reload(); }}>새로고침</button></div>
        <p className="iap-muted">전송할 IR 상태를 선택해 주세요.</p>
        {!result.loading && (result.error || reason) && <p className="iap-notice">{result.error || reason} 설정값은 미리 선택할 수 있으며, 장치가 준비되면 전송할 수 있습니다.</p>}
        <Choices label="전원" values={POWER} selected={state.power} onChange={(value) => update('power', value)} disabled={editingDisabled} />
        <Choices label="운전 모드" values={MODES} selected={state.mode} onChange={(value) => update('mode', value)} disabled={editingDisabled} />
        <Choices label="풍량" values={FANS} selected={state.fan} onChange={(value) => update('fan', value)} disabled={editingDisabled} />
        <div className="iap-temperature"><h3>설정 온도</h3><div><button aria-label="온도 낮추기" disabled={editingDisabled || state.temp <= 16} onClick={() => update('temp', state.temp - 1)}>−</button><output aria-label="설정 온도">{state.temp}<small>℃</small></output><button aria-label="온도 높이기" disabled={editingDisabled || state.temp >= 30} onClick={() => update('temp', state.temp + 1)}>+</button></div><p className="iap-footnote">16℃ ~ 30℃</p></div>
        <button className="iap-primary" disabled={disabled} onClick={send}>{busy ? '전송 결과 확인 중…' : 'IR 신호 전송'}</button>
        <p className={`iap-command-status ${command?.status === 'SUCCEEDED' ? 'success' : ''}`} role="status">{message || (command ? COMMAND_LABELS[command.status] : null) || (result.loading ? 'IR 제어 정보를 확인하고 있습니다.' : result.error || reason || 'IR 전송 준비 완료')}</p>
        {command?.status === 'SUCCEEDED' && <p className="iap-notice">모듈의 IR 신호 전송이 완료되었습니다. 실제 에어컨의 작동 상태는 확인되지 않습니다.</p>}
        {reason && command && <p className="iap-muted">{reason}</p>}
        {result.data?.last_successful && <p className="iap-footnote">마지막 IR 전송값 · {Object.values(result.data.last_successful.state).join(' · ')}℃<br />{formatKstDateTime(result.data.last_successful.sent_at, { withSeconds: true })}</p>}
    </section>;
}

function Detail({ client, deviceId, go, staff = false }) {
    const [tab, setTab] = useState('monitoring');
    const [days, setDays] = useState(1);
    const [metricKey, setMetricKey] = useState('pm2_5');
    const [refresh, setRefresh] = useState(0);
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const action = useAction();
    const loadDevice = useCallback((signal) => client.device(deviceId, signal), [client, deviceId]);
    const device = useRemote(loadDevice);
    const readMetric = staff ? metricKey : undefined;
    const loadReadings = useCallback((signal) => client.readings(deviceId, days, signal, readMetric), [client, deviceId, days, readMetric]);
    const readings = useRemote(loadReadings);
    const item = device.data?.device;
    const metric = APP_METRICS.find((entry) => entry.key === metricKey);
    const points = readings.data?.series?.[metricKey]?.filter((point) => typeof point.value === 'number' && Number.isFinite(point.value)) || [];
    const refreshAll = () => { device.reload(); readings.reload(); setRefresh((value) => value + 1); };
    return <>
        <div className="iap-section-heading"><span className="iap-muted">장치 상세</span><button className="iap-text-button" disabled={device.loading || action.busy} onClick={refreshAll}>새로고침</button></div>
        <RemoteState result={device} />
        {item && <>
            <DeviceSummary device={item} />
            <div className="iap-tabs" role="tablist" aria-label="장치 화면"><button role="tab" id="iap-monitoring-tab" aria-controls="iap-monitoring-panel" aria-selected={tab === 'monitoring'} onClick={() => setTab('monitoring')}>모니터링</button><button role="tab" id="iap-control-tab" aria-controls="iap-control-panel" aria-selected={tab === 'control'} onClick={() => setTab('control')}>제어</button></div>
            <div role="tabpanel" id="iap-monitoring-panel" aria-labelledby="iap-monitoring-tab" hidden={tab !== 'monitoring'}>
                <h2 className="iap-section-heading">현재 센서값</h2>
                <div className="iap-metrics">{APP_METRICS.map((entry) => <div className="iap-metric" key={entry.key}><h3>{entry.label}</h3><p><strong>{formatAppMetric(item.last_metrics?.[entry.key])}</strong><span>{entry.unit}</span></p></div>)}</div>
                <section className="iap-card iap-chart-card"><h2>센서 추이</h2><label className="iap-metric-picker">센서<select value={metricKey} onChange={(event) => setMetricKey(event.target.value)}>{APP_METRICS.map((entry) => <option value={entry.key} key={entry.key}>{entry.label}</option>)}</select></label>
                    <Choices label="조회 기간" values={[[1, '24시간'], [7, '7일'], [30, '30일']]} selected={days} onChange={setDays} />
                    <RemoteState result={readings} />
                    {!readings.loading && !readings.error && (points.length ? <div className="iap-chart" role="img" aria-label={`${metric.label} ${days === 1 ? '24시간' : `${days}일`} 추이, ${points.length}개 측정값`}><ResponsiveContainer width="100%" height="100%"><LineChart data={points} margin={{ top: 12, right: 8, bottom: 8, left: -18 }}><CartesianGrid vertical={false} stroke="#e5eaf0" /><XAxis dataKey="t" tickFormatter={formatChartTick} minTickGap={50} tick={{ fontSize: 10 }} /><YAxis width={58} tick={{ fontSize: 11 }} domain={['auto', 'auto']} /><Tooltip labelFormatter={(value) => formatKstDateTime(value)} formatter={(value) => [`${formatAppMetric(value)} ${metric.unit}`, metric.label]} /><Line type="linear" dataKey="value" stroke="#17644e" strokeWidth={2} dot={points.length === 1} isAnimationActive={false} /></LineChart></ResponsiveContainer></div> : <p className="iap-empty">선택한 기간의 측정값이 없습니다.</p>)}
                    <p className="iap-footnote">{days === 1 ? '원본 측정값' : '시간별 평균'} · {metric.unit}</p>
                </section>
            </div>
            <div role="tabpanel" id="iap-control-panel" aria-labelledby="iap-control-tab" hidden={tab !== 'control'}><IrControl client={client} deviceId={deviceId} refresh={refresh} visible={tab === 'control'} /></div>
            {!staff && <><section className="iap-management"><h2>장치 관리</h2><Alert>{action.error}</Alert>
                {item.role === 'OWNER' ? <>
                    <button className="iap-list-action" onClick={() => { setName(item.display_name || ''); setLocation(item.location || ''); setEditing(!editing); }}>장치 정보 수정 <span>›</span></button>
                    {editing && <form className="iap-card" onSubmit={(event) => { event.preventDefault(); action.run(async () => { await client.metadata(deviceId, { display_name: name.trim() || null, location: location.trim() || null }); setEditing(false); device.reload(); }); }}><label>표시명<input maxLength={100} value={name} onChange={(event) => setName(event.target.value)} /></label><label>위치<input maxLength={100} value={location} onChange={(event) => setLocation(event.target.value)} /></label><div className="iap-row"><button className="iap-primary" disabled={action.busy}>저장</button><button type="button" className="iap-secondary" disabled={action.busy} onClick={() => setEditing(false)}>취소</button></div></form>}
                    <button className="iap-list-action" onClick={() => go('sharing', deviceId)}>가족 공유 <span>›</span></button>
                    <button className="iap-list-action iap-danger" disabled={action.busy} onClick={() => { if (window.confirm('이 장치의 소유권을 해제할까요? 가족 공유도 함께 해제됩니다.')) action.run(async () => { await client.release(deviceId); go('hub'); }); }}>소유권 해제 <span>›</span></button>
                </> : <button className="iap-list-action iap-danger" disabled={action.busy} onClick={() => { if (window.confirm('이 장치의 가족 공유에서 나갈까요?')) action.run(async () => { await client.leave(deviceId); go('hub'); }); }}>공유에서 나가기 <span>›</span></button>}
            </section></>}
        </>}
    </>;
}

function Sharing({ client, deviceId }) {
    const [email, setEmail] = useState('');
    const action = useAction();
    const load = useCallback(async (signal) => {
        const [members, invitations] = await Promise.all([client.members(deviceId, signal), client.invitations(deviceId, signal)]);
        return { members: members.members, invitations: invitations.invitations };
    }, [client, deviceId]);
    const result = useRemote(load);
    return <>
        <h2>가족 공유</h2><p className="iap-muted">기존 고객 계정에 장치를 공유할 수 있습니다.</p>
        <form className="iap-card" onSubmit={(event) => { event.preventDefault(); action.run(async () => { await client.invite(deviceId, email); setEmail(''); result.reload(); }); }}><label>초대할 고객 이메일<input type="email" required maxLength={100} value={email} onChange={(event) => setEmail(event.target.value)} /></label><button className="iap-primary" disabled={action.busy || result.loading || Boolean(result.error)}>초대 보내기</button></form>
        <Alert>{action.error}</Alert><RemoteState result={result} />
        <div className="iap-section-heading"><h2>공유 중인 가족</h2><button className="iap-text-button" onClick={result.reload} disabled={result.loading || action.busy}>새로고침</button></div>
        {result.data?.members.length === 0 && <p className="iap-muted">공유 중인 가족이 없습니다.</p>}
        {result.data?.members.map((member) => <div className="iap-card" key={member.email}><h3>{member.user_name || member.email}</h3><p>{member.email}</p><button className="iap-secondary iap-danger" disabled={action.busy} onClick={() => { if (window.confirm('이 가족의 장치 공유를 해제할까요?')) action.run(async () => { await client.removeMember(deviceId, member.email); result.reload(); }); }}>공유 해제</button></div>)}
        <h2 className="iap-section-heading">보낸 초대</h2>
        {result.data?.invitations.length === 0 && <p className="iap-muted">대기 중인 초대가 없습니다.</p>}
        {result.data?.invitations.map((invitation) => <div className="iap-card" key={invitation.invitation_id}><h3>{invitation.email}</h3><p>수락 대기</p><button className="iap-secondary" disabled={action.busy} onClick={() => action.run(async () => { await client.cancelInvitation(deviceId, invitation.invitation_id); result.reload(); })}>초대 취소</button></div>)}
    </>;
}

export default function IotAppPrototype() {
    const [session, setSession] = useState(null);
    const [notice, setNotice] = useState('');
    const [screen, setScreen] = useState({ view: 'hub', deviceId: null });
    const contentRef = useRef(null);
    const client = useMemo(() => createIotAppClient(() => { setSession(null); setNotice('로그인이 만료되었습니다. 다시 로그인해 주세요.'); }), []);
    useEffect(() => () => client.clear(), [client]);
    useEffect(() => {
        if (!session) return undefined;
        const timer = setTimeout(() => { client.clear(); setSession(null); setNotice('로그인이 만료되었습니다. 다시 로그인해 주세요.'); }, Math.max(0, Date.parse(session.expiresAt) - Date.now()));
        return () => clearTimeout(timer);
    }, [client, session]);
    useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [screen, session]);
    const go = (view, deviceId = null, scan = false) => { setScreen({ view, deviceId, scan }); };
    const action = useAction();
    if (!isIotPrototypeHost(window.location.hostname)) return <main className="iap-shell"><p className="iap-empty">STG에서 제공되는 IoT 테스트 페이지입니다.</p></main>;
    return <div className="iap-shell"><div className="iap-phone"><div className="iap-display">
        <div className="iap-status-bar" aria-hidden="true">
            <span>9:41</span><span className="iap-camera-island" />
            <svg className="iap-status-icons" viewBox="0 0 57 16" fill="currentColor">
                <path d="M0 11h3v4H0zm5-3h3v7H5zm5-4h3v11h-3zm5-4h3v15h-3z" />
                <path d="M22 5q7-7 14 0l-2 2q-5-5-10 0zm3 4q4-4 8 0l-2 2q-2-2-4 0zm3 4 1-1 1 1-1 2z" />
                <rect x="39" y="3" width="15" height="10" rx="2" fill="none" stroke="currentColor" /><rect x="41" y="5" width="11" height="6" rx="1" /><path d="M55 6h2v4h-2z" />
            </svg>
        </div>
        <div className="iap-environment"><span>STG 테스트</span><span>IoT 모니터링 앱 · 프로토타입</span></div>
        <header className="iap-header">
            {session && screen.view !== 'hub' && <button aria-label="뒤로" onClick={() => go(screen.view === 'sharing' ? 'detail' : 'hub', screen.view === 'sharing' ? screen.deviceId : null)}>‹</button>}
            <h1>IoT 모니터링</h1>
            {session && <button className="iap-logout" disabled={action.busy} onClick={() => action.run(async () => { try { await client.logout(); setNotice(''); } finally { setSession(null); go('hub'); } })}>로그아웃</button>}
        </header>
        <main className="iap-content" ref={contentRef}><Alert>{action.error}</Alert>
            {!session ? <Login client={client} notice={notice} onLogin={(value) => { setSession(value); setNotice(''); go('hub'); }} /> : <>
                {screen.view === 'hub' && <Hub client={client} go={go} staff={session.kind === 'staff'} />}
                {session.kind !== 'staff' && screen.view === 'claim' && <Claim client={client} go={go} scanInitially={screen.scan} />}
                {screen.view === 'detail' && <Detail key={screen.deviceId} client={client} deviceId={screen.deviceId} go={go} staff={session.kind === 'staff'} />}
                {session.kind !== 'staff' && screen.view === 'sharing' && <Sharing key={screen.deviceId} client={client} deviceId={screen.deviceId} />}
            </>}
        </main>
        <div className="iap-home-indicator" aria-hidden="true" />
    </div></div></div>;
}
